import { SAMPLE_TEMPLATES } from '../constants';

type ViewMode = 'split' | 'editor' | 'preview' | 'zen' | 'presentation';

interface ToolbarProps {
  theme: 'github-light' | 'github-dark';
  onToggleTheme: () => void;
  onClear: () => void;
  onCopyHTML: () => void;
  onLoadSample: (sample: string) => void;
  isRendering: boolean;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

const VIEW_MODES = [
  { value: 'split' as const, label: '⚏ Split', title: 'Split view - Editor and preview side by side' },
  { value: 'editor' as const, label: '📝 Editor', title: 'Editor only - Focus on writing' },
  { value: 'preview' as const, label: '👁 Preview', title: 'Preview only - Reading mode' },
  { value: 'zen' as const, label: '🧘 Zen', title: 'Zen mode - Distraction-free writing' },
  { value: 'presentation' as const, label: '📊 Present', title: 'Presentation mode - Slideshow with navigation' },
];

export default function Toolbar({
  theme,
  onToggleTheme,
  onClear,
  onCopyHTML,
  onLoadSample,
  isRendering,
  viewMode,
  onViewModeChange,
}: ToolbarProps) {
  const currentViewMode = VIEW_MODES.find(v => v.value === viewMode) || VIEW_MODES[0];
  return (
    <div className="h-14 border-b border-gray-200 dark:border-gray-700 flex items-center px-4 gap-3 bg-white dark:bg-gray-800">
      <div className="flex items-center gap-3 mr-auto">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
          Markdawn
        </h1>
      </div>

      {isRendering && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          Rendering...
        </div>
      )}

      {/* View Mode Dropdown */}
      <div className="relative group">
        <button className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600">
          {currentViewMode.label} ▾
        </button>
        <div className="absolute left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
          {VIEW_MODES.map((mode) => (
            <button
              key={mode.value}
              onClick={() => onViewModeChange(mode.value)}
              className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-md last:rounded-b-md ${
                viewMode === mode.value
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300'
              }`}
              title={mode.title}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sample Templates Dropdown */}
      <div className="relative group">
        <button className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md">
          Load Sample ▾
        </button>
        <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
          {Object.entries(SAMPLE_TEMPLATES).map(([key, template]) => (
            <button
              key={key}
              onClick={() => onLoadSample(template.content)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-md last:rounded-b-md"
            >
              {template.name}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={onCopyHTML}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
      >
        Copy HTML
      </button>

      <button
        onClick={onClear}
        className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
      >
        Clear
      </button>

      <button
        onClick={onToggleTheme}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
        aria-label="Toggle theme"
      >
        {theme === 'github-light' ? '🌙 Dark' : '☀️ Light'}
      </button>

      {/* <a
          href="https://www.chai4.me/mdsiddiq"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          title="Support on chai4.me"
        >
        <span>☕</span>
      </a> */}
    </div>
  );
}
